import React, { useState, useEffect } from 'react';
import { arrayOf, bool, func, number, object, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import loadable from '@loadable/component';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { propTypes } from '../../util/types';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_NIGHT,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
} from '../../util/types';
import { required, moneySubUnitAmountAtLeast } from '../../util/validators';
import {
  stringifyDateToISO8601,
  timestampToDate
} from '../../util/dates';

import {
  Form,
  PrimaryButton,
  SecondaryButton,
  FieldCurrencyInput,
  FieldTextInput,
  H3,
  H6,
} from '../../components';

import css from './SendCustomOfferForm.module.css';
import appSettings from '../../config/settings';

const { Money } = sdkTypes;

// Lazy load the booking/product forms
const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ '../OrderPanel/BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ '../OrderPanel/BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ '../OrderPanel/BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ '../OrderPanel/ProductOrderForm/ProductOrderForm')
);

/**
 * Helper: Format field key from camelCase/snake_case to readable text
 */
const formatFieldKey = key => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Helper: Build field labels object from listing configuration and form values
 */
const buildFieldLabels = (listing, formValues) => {
  const labels = {};
  const unitType = listing.attributes.publicData.unitType;

  // Standard field labels by unit type
  const standardLabels = {
    bookingStart: 'Event Start',
    bookingEnd: 'Event End',
    bookingStartTime: 'Start Time',
    bookingEndTime: 'End Time',
    seats: 'Number of Guests',
    quantity: 'Quantity',
    deliveryMethod: 'Delivery Method',
    stockReservationQuantity: 'Quantity',
  };

  // Get custom field labels from listing configuration
  const customFields = listing.attributes.publicData.customFields || [];
  customFields.forEach(field => {
    labels[field.key] = field.label;
  });

  // Merge and format
  Object.keys(formValues).forEach(key => {
    if (!labels[key]) {
      labels[key] = standardLabels[key] || formatFieldKey(key);
    }
  });

  return labels;
};

/**
 * SendCustomOfferForm component
 *
 * Allows provider to send custom offer by:
 * - Filling the SAME form customer would see (with all validation)
 * - Entering custom price
 * - Adding personal message (required)
 * - Adding package description (optional)
 *
 * @param {Object} props
 * @param {Object} props.listing - The listing object
 * @param {Object} props.transaction - The inquiry transaction object
 * @param {Function} props.onSubmit - Handler when form is submitted
 * @param {Function} props.onCancel - Handler when form is cancelled
 * @param {Function} props.onFetchTimeSlots - Handler for fetching time slots (for bookings)
 * @param {Object} props.monthlyTimeSlots - Available time slots by month
 * @param {Object} props.timeSlotsForDate - Time slots for specific date
 * @param {string} props.marketplaceCurrency - Marketplace currency code
 * @param {number} props.dayCountAvailableForBooking - Days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 * @param {boolean} props.sendInProgress - Whether the offer is being sent
 */
const SendCustomOfferForm = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    listing,
    transaction,
    onSubmit,
    onCancel,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    sendInProgress,
  } = props;

  const [formData, setFormData] = useState({});

  const publicData = listing?.attributes?.publicData || {};
  const { unitType, priceVariants } = publicData;
  const price = listing?.attributes?.price;
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const lineItemUnitType = `line-item/${unitType}`;
  const currencyConfig = appSettings.getCurrencyFormatting(marketplaceCurrency);

  // Determine which form to show based on unit type
  const isBookingTime = [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const isBookingDates = [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const isBookingFixed = [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const isProduct = lineItemUnitType === LINE_ITEM_ITEM;

  // Handle when booking/product form values change
  const handleFormDataChange = (values) => {
    console.log('🔍 FormSpy values received:', JSON.stringify(values, null, 2));

    // Normalize field names from booking forms to expected format
    // BookingDatesForm/BookingTimeForm use nested structure: values.bookingDates.{startDate, endDate}
    // ProductOrderForm uses flat structure: values.{quantity, deliveryMethod}
    // We need to extract and rename to bookingStart/bookingEnd for the backend
    const bookingDates = values?.bookingDates || {};

    const normalizedValues = {
      ...values,
      // Day/night bookings: extract from nested bookingDates
      bookingStart: bookingDates.startDate || values.bookingStart,
      bookingEnd: bookingDates.endDate || values.bookingEnd,

      // Hourly bookings: extract timestamp fields
      bookingStartTime: values.bookingStartTime,
      bookingEndTime: values.bookingEndTime,

      // Seats might be top-level or nested
      seats: values.seats || bookingDates.seats,
    };

    console.log('✅ Normalized values:', {
      bookingStart: normalizedValues.bookingStart,
      bookingEnd: normalizedValues.bookingEnd,
      bookingStartTime: normalizedValues.bookingStartTime,
      bookingEndTime: normalizedValues.bookingEndTime,
      seats: normalizedValues.seats
    });

    setFormData(normalizedValues);
  };

  // Handle form submission
 const handleFormSubmit = values => {
  const { messageContent, offerDescription, customPrice: customPriceValue } = values;

  if (!customPriceValue || !messageContent) {
    return;
  }

  // FieldCurrencyInput already returns Money object with amount in subunits
  // No conversion needed - just use the amount directly
  const priceInSubunits = customPriceValue.amount;

  // Build field labels
  const fieldLabels = buildFieldLabels(listing, formData);

  // Normalize offer details based on listing unit type
  const unitType = listing?.attributes?.publicData?.unitType;
  let offerDetailsToSend = { ...formData };

  switch (unitType) {
    case 'item': {
      const quantity =
        formData?.stockReservationQuantity ??
        formData?.quantity ??
        1; // default fallback

      offerDetailsToSend = {
        unitType: 'item',
        stockReservationQuantity: quantity,
        deliveryMethod: formData?.deliveryMethod || 'shipping',
      };
      break;
    }

    case 'day':
    case 'night': {
      // The DateRangePicker already gives us dates with exclusive end date
      // We just need to serialize them to YYYY-MM-DD strings
      console.log('🔧 Day/Night booking - RAW values from form (already have exclusive end):', {
        bookingStart: formData?.bookingStart,
        bookingEnd: formData?.bookingEnd,
        unitType
      });

      // Just serialize to YYYY-MM-DD format
      const bookingStartStr = formData?.bookingStart
        ? stringifyDateToISO8601(formData.bookingStart, timeZone)
        : null;
      const bookingEndStr = formData?.bookingEnd
        ? stringifyDateToISO8601(formData.bookingEnd, timeZone)
        : null;

      console.log('📤 Day/Night booking - Serialized strings:', {
        bookingStartStr,
        bookingEndStr,
        note: 'End date is already exclusive from the picker'
      });

      offerDetailsToSend = {
        unitType,
        bookingStart: bookingStartStr,
        bookingEnd: bookingEndStr,
        seats: formData?.seats || 1,
      };
      break;
    }

    case 'hour': {
      // Convert timestamp strings to Date objects, then to ISO strings
      // BookingTimeForm uses bookingStartTime/bookingEndTime (timestamp strings)
      const startDate = formData?.bookingStartTime
        ? timestampToDate(formData.bookingStartTime)
        : formData?.bookingStart;
      const endDate = formData?.bookingEndTime
        ? timestampToDate(formData.bookingEndTime)
        : formData?.bookingEnd;

      // For hourly bookings, keep full ISO timestamp (includes time)
      const bookingStartStr = startDate ? startDate.toISOString() : null;
      const bookingEndStr = endDate ? endDate.toISOString() : null;

      offerDetailsToSend = {
        unitType: 'hour',
        bookingStart: bookingStartStr,  // Full ISO timestamp
        bookingEnd: bookingEndStr,      // Full ISO timestamp
        seats: formData?.seats || 1,
      };
      break;
    }

    case 'fixed': {
      offerDetailsToSend = {
        unitType: 'fixed',
        seats: formData?.seats || 1,
      };
      break;
    }

    case 'offer':
    case 'request':
    default: {
      offerDetailsToSend = {
        unitType: unitType || 'offer',
        quantity: 1,
      };
      break;
    }
  }

  // Submit the offer - MUST return the promise so parent can chain .then()
  return onSubmit({
    transactionId: transaction.id.uuid,
    listingId: listing.id.uuid,
    customPrice: priceInSubunits,
    messageContent,
    offerDescription: offerDescription || null,
    offerDetails: offerDetailsToSend,
    fieldLabels,
  });
};

  const classes = classNames(rootClassName || css.root, className);

  // Shared props for booking/product forms
  const sharedFormProps = {
    listing,
    listingId: listing.id,
    lineItemUnitType, // IMPORTANT: Needed for BookingDatesForm to determine day vs night
    price,
    marketplaceCurrency,
    marketplaceName,
    timeZone,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    dayCountAvailableForBooking,
    currentStock,
    isOwnListing: false, // Provider is viewing, but not as own listing
    // For ProductOrderForm - we don't need real-time line items in custom offer form
    // since provider will set custom price manually
    onFetchTransactionLineItems: () => {}, // Stub function
    lineItems: null,
    fetchLineItemsInProgress: false,
    fetchLineItemsError: null,
  };

  const messageContentLabel = intl.formatMessage({
    id: 'SendCustomOfferForm.messageContentLabel',
  });
  const messageContentPlaceholder = intl.formatMessage({
    id: 'SendCustomOfferForm.messageContentPlaceholder',
  });
  const offerDescriptionLabel = intl.formatMessage({
    id: 'SendCustomOfferForm.offerDescriptionLabel',
  });
  const offerDescriptionPlaceholder = intl.formatMessage({
    id: 'SendCustomOfferForm.offerDescriptionPlaceholder',
  });
  const customPriceLabel = intl.formatMessage({ id: 'SendCustomOfferForm.customPriceLabel' });
  const customPricePlaceholder = intl.formatMessage({
    id: 'SendCustomOfferForm.customPricePlaceholder',
  });

  return (
    <div className={classes}>
      <div className={css.header}>
        <H3>
          <FormattedMessage id="SendCustomOfferForm.title" />
        </H3>
      </div>

      {/* Step 1: Fill booking/product details (reused forms with validation) */}
      <div className={css.formSection}>
        {/* <H6 className={css.sectionTitle}>
          <FormattedMessage id="SendCustomOfferForm.step1Title" />
        </H6> */}

        {isBookingTime ? (
          <BookingTimeForm
            {...sharedFormProps}
            formId="SendCustomOfferBookingTimeForm"
            className={css.bookingForm}
            seatsEnabled={false}
            onChange={handleFormDataChange}
            onSubmit={handleFormDataChange}
          />
        ) : isBookingDates ? (
          <BookingDatesForm
            {...sharedFormProps}
            formId="SendCustomOfferBookingDatesForm"
            className={css.bookingForm}
            seatsEnabled={false}
            onChange={handleFormDataChange}
            onSubmit={handleFormDataChange}
          />
        ) : isBookingFixed ? (
          <BookingFixedDurationForm
            {...sharedFormProps}
            formId="SendCustomOfferBookingFixedForm"
            className={css.bookingForm}
            seatsEnabled={false}
            priceVariants={priceVariants}
            onChange={handleFormDataChange}
            onSubmit={handleFormDataChange}
          />
        ) : isProduct ? (
          <ProductOrderForm
            {...sharedFormProps}
            formId="SendCustomOfferProductForm"
            currentStock={currentStock}
            allowOrdersOfMultipleItems={true}
            pickupEnabled={publicData.pickupEnabled}
            shippingEnabled={publicData.shippingEnabled}
            displayDeliveryMethod={publicData.pickupEnabled || publicData.shippingEnabled}
            onChange={handleFormDataChange}
            onSubmit={handleFormDataChange}
          />
        ) : null}
      </div>

      {/* Step 2: Custom pricing and messages */}
      <FinalForm
        onSubmit={handleFormSubmit}
        render={({ handleSubmit, values }) => {
          const isSubmitDisabled =
            !values.customPrice ||
            !values.messageContent ||
            sendInProgress;

          return (
            <Form onSubmit={handleSubmit} className={css.offerForm}>
              <div className={css.formSection}>
                {/* <H6 className={css.sectionTitle}>
                  <FormattedMessage id="SendCustomOfferForm.step2Title" />
                </H6> */}

                {/* Custom Price */}
                <FieldCurrencyInput
                  id="customPrice"
                  name="customPrice"
                  className={css.priceField}
                  label={customPriceLabel}
                  placeholder={customPricePlaceholder}
                  currencyConfig={currencyConfig}
                  validate={required(
                    intl.formatMessage({ id: 'SendCustomOfferForm.customPriceRequired' })
                  )}
                />

                {/* Personal Message (Required) */}
                <FieldTextInput
                  id="messageContent"
                  name="messageContent"
                  type="textarea"
                  className={css.messageField}
                  label={messageContentLabel}
                  placeholder={messageContentPlaceholder}
                  validate={required(
                    intl.formatMessage({ id: 'SendCustomOfferForm.messageContentRequired' })
                  )}
                />

                {/* Package Description (Optional) */}
                <FieldTextInput
                  id="offerDescription"
                  name="offerDescription"
                  type="textarea"
                  className={css.descriptionField}
                  label={offerDescriptionLabel}
                  placeholder={offerDescriptionPlaceholder}
                />
              </div>

              {/* Action Buttons */}
              <div className={css.actionButtons}>
                <SecondaryButton type="button" onClick={onCancel} disabled={sendInProgress}>
                  <FormattedMessage id="SendCustomOfferForm.cancel" />
                </SecondaryButton>
                <PrimaryButton type="submit" inProgress={sendInProgress} disabled={isSubmitDisabled}>
                  <FormattedMessage id="SendCustomOfferForm.sendOffer" />
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    </div>
  );
};

SendCustomOfferForm.defaultProps = {
  rootClassName: null,
  className: null,
  monthlyTimeSlots: null,
  timeSlotsForDate: null,
  sendInProgress: false,
};

SendCustomOfferForm.propTypes = {
  rootClassName: string,
  className: string,
  listing: propTypes.listing.isRequired,
  transaction: propTypes.transaction.isRequired,
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  onFetchTimeSlots: func,
  monthlyTimeSlots: object,
  timeSlotsForDate: arrayOf(propTypes.timeSlot),
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,
  sendInProgress: bool,
};

export default SendCustomOfferForm;
